import { FC, useState } from 'react';
import { Delete, Visibility } from '@mui/icons-material';
import { numberToCurrencyString } from 'further-ui/utils';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Typography } from '@mui/material';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';

const pageId = 'unrealised-fx-history';

type Props = {
  vehicleId: string;
  onViewRecord?: (recordId: string) => void;
};

const UnrealisedFXGainOrLossSubTypeLabels = {
  FXGainPayable: 'FX Gain (Payable)',
  FXLossPayable: 'FX Loss (Payable)',
  FXGainReceivable: 'FX Gain (Receivable)',
  FXLossReceivable: 'FX Loss (Receivable)',
};

const PayablesOrReceivablesHistory: FC<Props> = ({
  vehicleId,
  onViewRecord,
}) => {
  const { vehicle } = useLayoutContext();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordIdToDelete, setRecordIdToDelete] = useState<string | null>(null);

  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'createdAt',
  });

  const { data, isLoading, refetch } =
    api.unrealisedFxGainOrLoss.listPosted.useQuery({
      vehicleId,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      order: pagination.order,
      orderBy: pagination.orderBy,
    });

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: deleteRecord } =
    api.unrealisedFxGainOrLoss.delete.useMutation(
      mutationOpts({
        successMessage: 'Unrealised FX gain/loss deleted successfully.',
        onSuccess: () => {
          refetch();
          setDeleteDialogOpen(false);
          setRecordIdToDelete(null);
        },
      }),
    );

  const handleOpenDeleteDialog = (recordId: string) => {
    setRecordIdToDelete(recordId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordIdToDelete(null);
  };

  const handleDelete = () => {
    if (recordIdToDelete) {
      deleteRecord({
        vehicleId,
        recordId: recordIdToDelete,
      });
    }
  };

  const rows = data?.results ?? [];

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'ledgerDate',
      label: 'Ledger Date',
      render: (row) => row.ledgerDate.toLabel(),
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      render: (row) => row.counterpartyLabel ?? '-',
      disableSorting: true,
    },
    {
      key: 'gainOrLossSubType',
      label: 'Type',
      render: (row) =>
        UnrealisedFXGainOrLossSubTypeLabels[row.gainOrLossSubType],
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) => {
        const isLoss =
          row.gainOrLossSubType === 'FXLossPayable' ||
          row.gainOrLossSubType === 'FXLossReceivable';
        const amount = isLoss ? -row.amountLcy : row.amountLcy;
        return numberToCurrencyString(amount, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
          negativeInBrackets: true,
        });
      },
      disableSorting: true,
    },
  ];

  if (!vehicle) {
    return null;
  }

  return (
    <Column spacing="lg">
      <Typography variant="h5">
        History of posted unrealised FX gains/losses
      </Typography>
      <Table
        actions={[
          {
            label: 'View',
            icon: Visibility,
            onClick: onViewRecord ? (row) => onViewRecord(row.id) : undefined,
          },
          {
            label: 'Delete',
            icon: Delete,
            disabled: (row) => !row.canDelete,
            tooltip: (row) =>
              row.canDelete ? undefined : row.deleteReason ?? 'Cannot delete',
            onClick: (row) => handleOpenDeleteDialog(row.id),
          },
        ]}
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        pagination={{
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          order: pagination.order,
          orderBy: pagination.orderBy,
          handleRequestSort: pagination.handleRequestSort,
          handleChangePage: pagination.handleChangePage,
          handleChangeRowsPerPage: pagination.handleChangeRowsPerPage,
        }}
        rowsTotalCount={data?.totalCount ?? 0}
      />
      <ConfirmDialog
        onClose={handleCloseDeleteDialog}
        open={deleteDialogOpen}
        title="Delete unrealised FX gain/loss?"
        content="Are you sure you want to delete this unrealised FX gain/loss? This cannot be undone."
        actions={{
          confirm: {
            onClick: handleDelete,
            label: 'Delete',
          },
        }}
      />
    </Column>
  );
};

export default PayablesOrReceivablesHistory;
